import { API_CONFIG, buildApiUrl } from '../config/api';
import { ServiceRequest, ServiceRequestsResponse } from '../models/ServiceRequest';
import { secureTokenManager } from '../utils/secureTokenManager';

export class RequestService {
  private static baseURL = API_CONFIG.baseURL;

  /**
   * Fetch all service requests from the API
   */
  static async getAllRequests(): Promise<ServiceRequestsResponse> {
    const url = buildApiUrl('/service-users/all-requests');

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${secureTokenManager.getAdminSessionToken()}`,
          // Cache-busting headers to prevent browser caching
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          // Remove Expires header to avoid CORS issues
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ServiceRequestsResponse = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get a specific request by ID
   */
  static async getRequestById(requestId: string): Promise<ServiceRequest> {
    const url = buildApiUrl('/service-users/all-requests');

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${secureTokenManager.getAdminSessionToken()}`,
          // Cache-busting headers to prevent browser caching
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          // Remove Expires header to avoid CORS issues
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ServiceRequestsResponse = await response.json();
      const request = data.data.serviceRequests.find((req: ServiceRequest) => req.requestId === requestId);

      if (!request) {
        throw new Error('Request not found');
      }

      return request;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get detailed information about a specific service request
   */
  static async getRequestDetails(requestId: string): Promise<ServiceRequest> {
    const url = buildApiUrl(`/service-users/request/${requestId}`);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${secureTokenManager.getAdminSessionToken()}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update request status
   */
  static async updateRequestStatus(
    requestId: string,
    status: string
  ): Promise<void> {
    const url = buildApiUrl('/service-users/all-requests');

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${secureTokenManager.getAdminSessionToken()}`,
        },
        body: JSON.stringify({
          requestId,
          status,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      throw error;
    }
  }
}
